/**
 * Seção "Você se reconhece?": dores do dia a dia do dono do negócio e
 * como o Vende Aí resolve cada uma
 */

import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { PAIN_POINTS, PainPoint } from '../content';
import { Reveal, SectionHeading } from './Reveal';

interface PainPointsProps {
  items?: PainPoint[];
  eyebrow?: string;
  title?: string;
  highlight?: string;
  description?: string;
  /** Âncora do link "Calcule sua economia" (omitido quando não há calculadora) */
  savingsHref?: string;
}

export function PainPoints({
  items = PAIN_POINTS,
  eyebrow = 'Você se reconhece?',
  title = 'O dia a dia que faz você',
  highlight = 'perder vendas e dinheiro',
  description = 'Se alguma dessas situações acontece no seu negócio, o problema não é falta de esforço: é falta de sistema.',
  savingsHref = '#economia',
}: PainPointsProps) {
  return (
    <section id="dores" className="relative scroll-mt-20 py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow={eyebrow}
          title={
            <>
              {title} <span className="vw-gradient-text">{highlight}</span>
            </>
          }
          description={description}
        />

        <ul className="mt-10 grid gap-3 sm:mt-12 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4">
          {items.map((item, i) => (
            <Reveal
              as="li"
              key={item.pain}
              delay={(i % 4) * 60}
              className="flex flex-col rounded-3xl border border-white/10 bg-gradient-to-b from-white/[0.05] to-transparent p-5 sm:p-6"
            >
              <div className="flex items-center gap-3 sm:block">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-rose-500/10 text-rose-300 sm:h-11 sm:w-11">
                  <item.icon size={20} aria-hidden />
                </span>
                <h3 className="font-semibold leading-snug text-white sm:mt-5">{item.pain}</h3>
              </div>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-400 sm:mt-2">
                {item.description}
              </p>
              <p className="mt-4 flex gap-2 border-t border-white/10 pt-3 text-sm font-medium text-emerald-300 sm:mt-5 sm:pt-4">
                <CheckCircle2 size={16} className="mt-0.5 shrink-0" aria-hidden />
                <span>{item.solution}</span>
              </p>
            </Reveal>
          ))}
        </ul>

        {savingsHref && (
          <Reveal className="mt-10 text-center">
            <a
              href={savingsHref}
              className="group inline-flex items-center gap-2 rounded-2xl border border-white/15 bg-white/5 px-6 py-3.5 font-semibold text-white transition hover:bg-white/10"
            >
              Calcule quanto você perde em comissões
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </a>
          </Reveal>
        )}
      </div>
    </section>
  );
}
